import { useState } from 'react';
import { Link, ui } from '../ui';
import { signIn } from '../auth';
import { currentSite } from '../router';
import { AuthScreen, Field, Notice, returnTo } from './shared';
import { SocialSignIn } from './providers';

/**
 * Signing in.
 *
 * This page did not exist: Clerk supplied it, it lived in the Next app, and it
 * went with it. Self-hosting identity means owning the form as well as the
 * session, which is the part of that migration that was real work rather than a
 * swap.
 *
 * **It used to be signing up as well**, one component with a `mode` prop,
 * because the two forms differed by one field and a verb and keeping them
 * together was what stopped the copy, the error handling and the layout
 * drifting apart. That argument held for exactly as long as the difference was
 * one field. Signing up now asks five questions — the terms, an age, a country,
 * a route in, and only then an address — and a `mode` boolean threaded through
 * that would be two screens sharing a function rather than a shape. See
 * `./sign-up`. What they still share is `AuthScreen`, `returnTo` and the
 * provider buttons, which is the part that was actually worth sharing.
 *
 * **Errors say what the server said.** Better Auth distinguishes a wrong
 * password from an unknown account internally and deliberately returns the same
 * message for both, which is correct — telling an anonymous caller which
 * addresses exist is an enumeration oracle. So there is nothing to improve on
 * here beyond showing it.
 */
export function SignInRoute(): React.ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const site = currentSite();
  /*
    Where signing in lands, per ground.

    `developers` used to send somebody to `/portal` and #144 removed it, so that
    branch is gone rather than repointed: there is no workshop screen to arrive
    at, and `/library` is what somebody on any ground can always open.
  */
  const home = site === 'creators' ? '/board' : '/library';

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    setBusy(true);
    setError(null);

    const result = await signIn.email({
      email: String(form.get('email') ?? ''),
      password: String(form.get('password') ?? ''),
    });

    if (result.error) {
      setError(result.error.message ?? 'That did not work. Try again.');
      setBusy(false);
      return;
    }

    // A full navigation rather than a router push: the whole app is behind the
    // session, and reloading is the simplest way to be sure nothing rendered
    // before it existed is still on screen.
    window.location.href = returnTo() ?? home;
  }

  return (
    <AuthScreen
      title="Sign in"
      foot={
        <>
          <p>
            <Link href="/forgot-password">Forgotten your password?</Link>
          </p>
          <p>
            No account yet? <Link href="/sign-up">Create one</Link>.
          </p>
        </>
      }
    >
      <form className={ui.stack ?? ''} onSubmit={(event) => void submit(event)}>
        {error ? <Notice alert>{error}</Notice> : null}

        <Field label="Email">
          <input
            className={ui.input ?? ''}
            name="email"
            type="email"
            required
            autoComplete="email"
          />
        </Field>

        <Field label="Password">
          <input
            className={ui.input ?? ''}
            name="password"
            type="password"
            required
            minLength={8}
            autoComplete="current-password"
          />
        </Field>

        <button type="submit" className={ui.button ?? ''} disabled={busy}>
          {busy ? 'One moment…' : 'Sign in'}
        </button>
      </form>

      {/*
        Below the form, never above it. Hard rule 22: no social account is ever
        required to buy a game, and putting these first would make the email
        form read as the fallback rather than as the equal it is. Renders
        nothing at all when the deployment has no provider credentials.
      */}
      <SocialSignIn mode="sign-in" />
    </AuthScreen>
  );
}
